import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, X, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackBeginCheckout } from "@/lib/dataLayer";
import { getStoredGclid } from "@/lib/attribution";
import { getReferralId } from "@/lib/referral";
import {
  originatingProjectId,
  saveCheckoutReturn,
} from "@/lib/checkoutReturn";
import {
  CREDIT_PACK_ORDER,
  BEST_VALUE_PACK_ID,
  type CreditPackId,
} from "@/lib/pricingPlans";

// Order packs render in. Pack metadata (credits, price, discount label) comes
// from the server via `payment.getCreditPacks` so we stay in sync with the
// canonical CREDIT_PACKS in server/stripe.ts.
const PACK_ORDER = CREDIT_PACK_ORDER;
type PackId = CreditPackId;

type Filter = "all" | "spent" | "earned";

function formatTimestamp(date: Date | string | null) {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatAmount(amount: number) {
  const sign = amount >= 0 ? "+" : "-";
  return `${sign}${Math.abs(amount).toFixed(2)}`;
}

interface CreditHistoryModalProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: "topup" | "details";
}

export default function CreditHistoryModal({
  open,
  onClose,
  defaultTab = "details",
}: CreditHistoryModalProps) {
  const [topTab, setTopTab] = useState<"topup" | "details">(defaultTab);

  // Re-sync the tab to `defaultTab` whenever the modal is opened, so callers
  // that want to land the user on "topup" always get it even after prior use.
  useEffect(() => {
    if (open) setTopTab(defaultTab);
  }, [open, defaultTab]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="max-w-2xl max-h-[85vh] overflow-hidden p-0 gap-0 bg-[oklch(0.14_0.02_270)] border-white/10"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <Tabs
            value={topTab}
            onValueChange={(v) => setTopTab(v as "topup" | "details")}
            className="flex-1"
          >
            <TabsList className="mx-auto bg-white/5">
              <TabsTrigger value="topup" className="px-5">
                Top-up credits
              </TabsTrigger>
              <TabsTrigger value="details" className="px-5">
                My credits details
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 shrink-0 ml-2"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {topTab === "topup" ? <TopUpPanel /> : <DetailsPanel />}
      </DialogContent>
    </Dialog>
  );
}

function TopUpPanel() {
  const [loadingId, setLoadingId] = useState<PackId | null>(null);
  const { data: packs, isLoading } = trpc.payment.getCreditPacks.useQuery();
  const createCheckout = trpc.payment.createCreditPackCheckout.useMutation();

  const handleBuy = async (packId: PackId) => {
    setLoadingId(packId);
    const pack = packs?.[packId];
    if (pack) {
      trackBeginCheckout(
        [
          {
            item_id: `credits_${packId}`,
            item_name: `Credits ${packId} (${pack.credits.toLocaleString()})`,
            price: pack.price,
            quantity: 1,
            item_category: "credits",
          },
        ],
        pack.price,
        "USD",
        { source: "credit_history_modal", trigger: "topup_panel" },
      );
    }
    try {
      const returnProjectId = originatingProjectId();
      const result = await createCheckout.mutateAsync({
        packId,
        gclid: getStoredGclid() ?? undefined,
        clientReferenceId: getReferralId(),
        returnProjectId,
      });
      saveCheckoutReturn(result, { returnProjectId });
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err: any) {
      // Backend rejects with FORBIDDEN if user has no active paid sub.
      toast.error(err?.message ?? "Checkout failed");
    } finally {
      setLoadingId(null);
    }
  };

  if (isLoading || !packs) {
    return (
      <div className="px-6 pb-6 pt-2 flex items-center justify-center min-h-[280px]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 pt-2 space-y-3">
      <p className="text-xs text-muted-foreground -mt-1 mb-1">
        One-time packs, no subscription required. Credits are valid for 12
        months.
      </p>
      {PACK_ORDER.map((packId) => {
        const pack = packs[packId];
        if (!pack) return null;
        const isBest = packId === BEST_VALUE_PACK_ID;
        const perCredit = pack.price / pack.credits;
        return (
          <div
            key={packId}
            className={cn(
              "flex items-center justify-between rounded-xl border p-4",
              isBest
                ? "border-purple-500/40 bg-gradient-to-r from-purple-900/30 to-violet-900/20"
                : "border-white/10 bg-white/[0.03]",
            )}
          >
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "h-10 w-10 rounded-full flex items-center justify-center",
                  isBest ? "bg-purple-500/30" : "bg-violet-600/20",
                )}
              >
                <Zap
                  className={cn(
                    "h-5 w-5",
                    isBest ? "text-purple-300" : "text-violet-400",
                  )}
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-foreground">
                    {pack.credits.toLocaleString()} credits
                  </div>
                  {"discount" in pack && pack.discount && (
                    <span
                      className={cn(
                        "text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                        isBest
                          ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                          : "bg-green-500/20 text-green-400",
                      )}
                    >
                      {isBest ? "BEST VALUE" : pack.discount}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  ${perCredit.toFixed(4)} per credit
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-semibold text-foreground">
                  ${pack.price}
                </div>
              </div>
              <Button
                onClick={() => handleBuy(packId)}
                disabled={loadingId !== null}
                size="sm"
                className={cn(
                  "min-w-[72px]",
                  isBest &&
                    "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white",
                )}
              >
                {loadingId === packId ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Buy"
                )}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DetailsPanel() {
  const [filter, setFilter] = useState<Filter>("all");
  const scrollRef = useRef<HTMLDivElement>(null);

  const query = trpc.payment.creditHistory.useInfiniteQuery(
    { filter, limit: 30 },
    {
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    },
  );

  // Reset scroll on filter change.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [filter]);

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 200 &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      query.fetchNextPage();
    }
  };

  return (
    <>
      <div className="px-6">
        <div className="flex gap-6 border-b border-white/10">
          {(["all", "spent", "earned"] as const).map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                "relative py-2 text-sm capitalize transition-colors",
                filter === key
                  ? "text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {key === "all" ? "All" : key === "spent" ? "Spent" : "Earned"}
              {filter === key && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-violet-500" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto max-h-[60vh] min-h-[320px]"
      >
        {query.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-8">
            <div className="text-sm text-muted-foreground">
              No credit activity yet
            </div>
            <div className="mt-1 text-xs text-muted-foreground/70">
              Transactions will appear here as you use or earn credits
            </div>
          </div>
        ) : (
          <div>
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-4 px-6 py-3 border-b border-white/5 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-foreground truncate">
                    {item.label}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {formatTimestamp(item.createdAt)}
                  </div>
                </div>
                <div
                  className={cn(
                    "font-semibold tabular-nums shrink-0",
                    item.amount >= 0 ? "text-green-400" : "text-foreground",
                  )}
                >
                  {formatAmount(item.amount)}
                </div>
              </div>
            ))}
            {query.isFetchingNextPage && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
